import { GoodsFamily } from '@/common'
import { isSupplierEmissionsReportEligibleForCbamReport } from '@artem-cbam-declarants/shared'
import { getRequiredGoodsFamiliesForCnCodes } from './requiredCnCodesForBatchUpdate'

/** i18n key for Supplier & Emissions button when CoO is incompatible */
export const SUPPLIER_EMISSIONS_BUTTON_TOOLTIP_COO_INCOMPATIBLE =
  'cbamReporting:bulkUpdate.supplierEmissionsButtonTooltipCoOIncompatible'

export type InstallationIneligibilityReason =
  | 'CONFLICTING_ECONOMIC_ACTIVITIES'
  | 'NO_INSTALLATION_IN_COUNTRY_SUPPORTS_REQUIRED_ACTIVITIES'
  | 'NO_SINGLE_INSTALLATION_SUPPORTS_ALL_REQUIRED_ACTIVITIES'

/** i18n keys for installation ineligibility (button tooltip and dialog alerts) */
export const INSTALLATION_INELIGIBILITY_STRINGS: Record<InstallationIneligibilityReason, string> = {
  CONFLICTING_ECONOMIC_ACTIVITIES:
    'cbamReporting:bulkUpdate.installationIneligibility.conflictingEconomicActivities',
  NO_INSTALLATION_IN_COUNTRY_SUPPORTS_REQUIRED_ACTIVITIES:
    'cbamReporting:bulkUpdate.installationIneligibility.noInstallationInCountrySupportsRequiredActivities',
  NO_SINGLE_INSTALLATION_SUPPORTS_ALL_REQUIRED_ACTIVITIES:
    'cbamReporting:bulkUpdate.installationIneligibility.noSingleInstallationSupportsAllRequiredActivities'
}

/** Used by getInstallationEligibility to resolve CN code → aggregated goods category (e.g. from goods config). */
export type GetAggregatedCategoryForCnCode = (cnCode: string) => string | undefined

/** Minimal shape for selected goods in button/eligibility logic */
export type SelectedGoodCountryCn = { country: string; cnCode: string }
type SelectedGoodCn = { cnCode: string }
type SelectedGoodForBatchRemoveActions = {
  partnershipId?: string | null
  installationId?: string | null
  supplierEmissionsReportId?: string | null
  importerId?: string | null
  requestedCustomsProcedure?: string | null
  previousCustomsProcedure?: string | null
}

type BatchRemoveActionsAvailability = {
  canRemoveSupplierInstallation: boolean
  canRemoveEmissionsReport: boolean
  canRemoveImporter: boolean
  canRemoveRequestedProcedure: boolean
  canRemovePreviousProcedure: boolean
}

/** Installation minimal shape for eligibility */
export type InstallationForEligibility = {
  id: string
  country: string
  goodsFamily: GoodsFamily
  partnershipId: string
}

/** Emissions report minimal shape for eligibility */
type EmissionsReportForEligibility = {
  id: string
  installationId: string
  periodId: string
  goods: { cnCode: string }[]
}

type GetSupplierEmissionsButtonStateParams = {
  selectedGoods: SelectedGoodCountryCn[]
  installations?: InstallationForEligibility[]
  getAggregatedCategoryForCnCode?: GetAggregatedCategoryForCnCode
}

/**
 * Supplier & Emissions button enablement. Disabled when selected goods have mixed or incompatible Country of Origin,
 * or when no installation is eligible (ineligibility reason shown as tooltip).
 */
export function getSupplierEmissionsButtonState(params: GetSupplierEmissionsButtonStateParams): {
  disabled: boolean
  tooltipKey: string | null
} {
  const { selectedGoods, installations, getAggregatedCategoryForCnCode } = params

  if (selectedGoods.length === 0) {return { disabled: false, tooltipKey: null }}

  const countries = selectedGoods.map(good => (good.country ?? '').trim())
  const allEmpty = countries.every(country => !country)
  const first = countries[0]
  const allSame = countries.every(country => country === first)

  if (!allEmpty && (!first || !allSame)) {
    return {
      disabled: true,
      tooltipKey: SUPPLIER_EMISSIONS_BUTTON_TOOLTIP_COO_INCOMPATIBLE
    }
  }

  if (installations != null && installations.length > 0 && getAggregatedCategoryForCnCode != null) {
    const { ineligibilityReason } = getInstallationEligibility({
      selectedGoods,
      installations,
      getAggregatedCategoryForCnCode
    })
    if (ineligibilityReason != null) {
      return {
        disabled: true,
        tooltipKey: INSTALLATION_INELIGIBILITY_STRINGS[ineligibilityReason]
      }
    }
  }

  return { disabled: false, tooltipKey: null }
}

function hasNonEmptyValue(value?: string | null): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Field-level availability for "Remove ..." options inside batch update dialogs.
 * A remove action is available when at least one selected good has a value for that field.
 */
export function getBatchRemoveActionsAvailability(
  selectedGoods: SelectedGoodForBatchRemoveActions[]
): BatchRemoveActionsAvailability {
  return {
    canRemoveSupplierInstallation: selectedGoods.some(
      good => hasNonEmptyValue(good.partnershipId) || hasNonEmptyValue(good.installationId)
    ),
    canRemoveEmissionsReport: selectedGoods.some(good =>
      hasNonEmptyValue(good.supplierEmissionsReportId)
    ),
    canRemoveImporter: selectedGoods.some(good => hasNonEmptyValue(good.importerId)),
    canRemoveRequestedProcedure: selectedGoods.some(good =>
      hasNonEmptyValue(good.requestedCustomsProcedure)
    ),
    canRemovePreviousProcedure: selectedGoods.some(good =>
      hasNonEmptyValue(good.previousCustomsProcedure)
    )
  }
}

/**
 * Installation eligibility for the Supplier & Emissions batch dialog.
 * Uses getRequiredGoodsFamiliesForCnCodes from requiredCnCodesForBatchUpdate (Option A);
 * the caller injects only getAggregatedCategoryForCnCode (e.g. from goods config).
 */
export function getInstallationEligibility(params: {
  selectedGoods: SelectedGoodCountryCn[]
  installations: InstallationForEligibility[]
  getAggregatedCategoryForCnCode: GetAggregatedCategoryForCnCode
}): {
  eligibleInstallationIds: string[]
  ineligibilityReason: InstallationIneligibilityReason | null
} {
  const { selectedGoods, installations, getAggregatedCategoryForCnCode } = params

  const cnCodes = selectedGoods.map(good => good.cnCode)
  const requiredFamilies = getRequiredGoodsFamiliesForCnCodes(
    cnCodes,
    getAggregatedCategoryForCnCode
  )

  if (requiredFamilies.length === 0) {
    const noCnCodeFilter = cnCodes.every(code => !(code && code.trim()))
    if (noCnCodeFilter) {
      const countryOfOrigin = getCommonCountryOfOrigin(selectedGoods)
      const filtered =
        countryOfOrigin != null
          ? installations.filter(inst => inst.country === countryOfOrigin)
          : installations
      return {
        eligibleInstallationIds: filtered.map(inst => inst.id),
        ineligibilityReason: null
      }
    }
    return {
      eligibleInstallationIds: [],
      ineligibilityReason: 'CONFLICTING_ECONOMIC_ACTIVITIES'
    }
  }

  const countryOfOrigin = getCommonCountryOfOrigin(selectedGoods)
  let filteredInstallations = installations

  if (countryOfOrigin != null) {
    filteredInstallations = installations.filter(inst => inst.country === countryOfOrigin)
  }

  const requiredSet = new Set(requiredFamilies)
  const eligible = filteredInstallations.filter(inst => requiredSet.has(inst.goodsFamily))

  if (eligible.length > 0) {
    return {
      eligibleInstallationIds: eligible.map(inst => inst.id),
      ineligibilityReason: null
    }
  }

  if (requiredFamilies.length > 1) {
    return {
      eligibleInstallationIds: [],
      ineligibilityReason: 'NO_SINGLE_INSTALLATION_SUPPORTS_ALL_REQUIRED_ACTIVITIES'
    }
  }

  return {
    eligibleInstallationIds: [],
    ineligibilityReason: 'NO_INSTALLATION_IN_COUNTRY_SUPPORTS_REQUIRED_ACTIVITIES'
  }
}

function getCommonCountryOfOrigin(goods: SelectedGoodCountryCn[]): string | null {
  if (goods.length === 0) {return null}
  const countries = goods.map(good => (good.country ?? '').trim())
  const allEmpty = countries.every(country => !country)
  if (allEmpty) {return null}
  const first = countries[0]
  const allSame = countries.every(country => country === first)
  return allSame ? first || null : null
}

/** i18n key for emissions report "same CN code" message */
export const EMISSIONS_REPORT_TOOLTIP_DIFFERENT_CN_CODES =
  'cbamReporting:bulkUpdate.emissionsReportTooltipDifferentCnCodes'

export function getNoEmissionsReportsAvailableWarningKey(cbamReportYear?: number): string {
  if (cbamReportYear === 2026) {
    return 'cbamReporting:bulkUpdate.noEmissionsReportsAvailable2026'
  }

  if (cbamReportYear === 2027) {
    return 'cbamReporting:bulkUpdate.noEmissionsReportsAvailable2027'
  }

  return 'cbamReporting:bulkUpdate.noEmissionsReportsAvailable'
}

/**
 * Emissions report eligibility: selectable only when installation is selected and all selected goods share the same CN code.
 */
export function getEmissionsReportEligibility(params: {
  selectedGoods: SelectedGoodCn[]
  selectedInstallationId: string | null
  emissionsReports: EmissionsReportForEligibility[]
  cbamReportYear?: number
}): {
  canSelect: boolean
  tooltipKey: string | null
  eligibleReportIds: string[]
} {
  const { selectedGoods, selectedInstallationId, emissionsReports, cbamReportYear } = params

  if (selectedInstallationId == null) {
    return { canSelect: false, tooltipKey: null, eligibleReportIds: [] }
  }

  const cnCodes = selectedGoods.map(good => good.cnCode)
  const uniqueCn = [...new Set(cnCodes)]
  if (uniqueCn.length > 1) {
    return {
      canSelect: false,
      tooltipKey: EMISSIONS_REPORT_TOOLTIP_DIFFERENT_CN_CODES,
      eligibleReportIds: []
    }
  }

  const commonCn = uniqueCn[0]
  const byInstallation = emissionsReports.filter(
    report => report.installationId === selectedInstallationId
  )
  const noCnCodeFilter = !commonCn || !commonCn.trim()
  const byCnCode = noCnCodeFilter
    ? byInstallation
    : byInstallation.filter(report =>
        report.goods.some(good => good.cnCode === commonCn)
      )

  const eligible =
    cbamReportYear === undefined
      ? byCnCode
      : byCnCode.filter(report =>
          isSupplierEmissionsReportEligibleForCbamReport(report.periodId, cbamReportYear)
        )

  return {
    canSelect: true,
    tooltipKey: null,
    eligibleReportIds: eligible.map(report => report.id)
  }
}
